import {api} from 'nutshell-core/api';
import {convertToObj} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

/**
 * GET /rest/leads/installments/report — matches the products report URL shape
 * (`/rest/leads/products/report`).
 */
export const INSTALLMENTS_REPORT_PATH = 'leads/installments/report';

export type InstallmentsReportFacet = 'total' | 'count';

export type InstallmentsChartBucket = {
    key: string;
    label: string;
    startTime: number;
    endTime: number;
    collected: number;
    planned: number;
    pipelines: number;
    failed: number;
    total: number;
};

export type InstallmentsReportApiResponse = {
    summary: {
        facet: InstallmentsReportFacet;
        collected: number;
        planned: number;
        pipelines: number;
        failed: number;
        total: number;
    };
    chart: {
        gap: string;
        facet: InstallmentsReportFacet;
        buckets: InstallmentsChartBucket[];
    };
    details: {
        facet: InstallmentsReportFacet;
        rows: Array<{
            label: string;
            collected: number;
            planned: number;
            pipelines: number;
            failed: number;
            total: number;
        }>;
        totals: {
            collected: number;
            planned: number;
            pipelines: number;
            failed: number;
            total: number;
        };
    };
    filtersApplied: {
        gap: string;
        facet: InstallmentsReportFacet;
        includeOpenLeads: boolean;
    };
};

export function fetchInstallmentsReport(options: {
    activeFilters: FilterObject[];
    gap: string;
    facet?: InstallmentsReportFacet;
    includeOpenLeads?: boolean;
    signal?: AbortSignal;
}): Promise<InstallmentsReportApiResponse> {
    const {activeFilters, gap, facet = 'total', includeOpenLeads = false, signal} = options;
    return api
        .get(
            INSTALLMENTS_REPORT_PATH,
            {
                filter: convertToObj(activeFilters),
                gap,
                facet,
                includeOpenLeads,
            },
            signal ? {signal} : undefined
        )
        .then((response: unknown) => (response as Response).json());
}
